import { type FC } from "react";
import { TableCell } from "~/common/components/Table/TableElements";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import { ParamComponent } from "~/common/components/ParamsGrid";
import { CellWithSubtitle } from "~/features/productDetails/components/DataTables/CellWithSubtitle";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { AliasesParameter } from "~/domains/product/components/ProductParams";
import { Stack } from "~/common/mui";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import ItemBlock from "~/common/components/print/ItemBlock";
import { type ShippingItem } from "~/features/orders/orderDetails/components/ShipmentDetails/PackageTable";
import { type ProductData } from "~/domains/product/types/ProductData";
import { resolveUrl } from "~/utils/url/urlResolver";
import { fakeDescriptorFromProduct } from "~/domains/product/utils/productDescriptorUtils";
import { Translation } from "~/app/i18n/Translation";
import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import { OrderLineItemElements } from "~/features/orders/common/components/OrderLineItemElements";
import { type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { useTranslation } from "react-i18next";
import { translateWarehouseName } from "~/utils/language/translationUtils";

interface PackageTableProductRowProps {
  item: ShippingItem;
}

const PackageTableProductRow: FC<PackageTableProductRowProps> = ({ item }) => {
  const { t } = useTranslation();
  const orderEntry: OrderLineItemData = item.entry;
  const product: ProductData = orderEntry.product;
  const { actualGIDate, plannedGIDate } = item;

  const goodsIssueDate = () => {
    const date = actualGIDate || plannedGIDate;
    return date ? formatNullableDate(date, DateFormatType.DATE) : "-";
  };

  return (
    <>
      <TableCell className={"scha__order-details-page-shipment-row__cell__product"} style={{ height: "fit-content" }}>
        <ItemBlock
          className={"scha__print-only"}
          mainTitle={t("quoteDetailsPage.table.header.product")}
          mainValue={" "}
        />
        <div className="scha__order-table__product">
          <a href={resolveUrl(product.url)}>
            <ProductDesignation
              isPreferred={product.preferred}
              designation={product.designation}
              size={"title-medium"}
            />
          </a>
          {product.shortDescription && (
            <div className="scha__order-table__product__description">
              <Translation {...product.shortDescription} />
            </div>
          )}
          {product.sapMaterialNumber && (
            <ParamComponent label={t("cartPage.table.header.schaId.title")} value={product.sapMaterialNumber} />
          )}
          <AliasesParameter className={"my-15"} product={orderEntry.product} mode={"13-digits"} />
          {isLineLevelCustomerOrderNumberEnabled() && orderEntry.customerOrderNumber && (
            <ParamComponent label={t("search.type.customerOrderNumber")} value={orderEntry.customerOrderNumber} />
          )}
        </div>
        <ItemBlock
          className={"scha__print-only mt-15"}
          mainTitle={t("quoteDetailsPage.table.header.quantity")}
          secondTitle={t("quoteDetailsPage.summary.shippedQuantity")}
          mainValue={orderEntry.qty}
          secondValue={orderEntry.shippedQty}
        />
      </TableCell>
      <TableCell className={"scha__screen-only"} style={{ height: "fit-content" }}>
        <CellWithSubtitle title={orderEntry.qty} subtitle={orderEntry.shippedQty} lightHeader={true} />
      </TableCell>
      <TableCell className={"scha__order-details-page-shipment-row__cell__packaging"} style={{ height: "fit-content" }}>
        <ItemBlock
          className={"scha__print-only"}
          mainTitle={t("quoteDetailsPage.summary.packagingCode")}
          secondTitle={t("cartPage.table.header.packaging.subtitle")}
          mainValue={getPackagingLabelAtLineItem({
            t,
            productDescriptor: fakeDescriptorFromProduct(product),
            quantity: orderEntry.qty,
            packCode: orderEntry.packCode,
            packSize: orderEntry.packSize,
          })}
          secondValue={translateWarehouseName(t, orderEntry.location)}
        />
        <Stack direction="column" gap={2.5}>
          <CellWithSubtitle
            className={"scha__screen-only"}
            title={getPackagingLabelAtLineItem({
              t,
              productDescriptor: fakeDescriptorFromProduct(product),
              quantity: orderEntry.qty,
              packCode: orderEntry.packCode,
              packSize: orderEntry.packSize,
            })}
            subtitle={translateWarehouseName(t, orderEntry.location)}
            lightHeader={true}
          />
          <AliasesParameter
            className={"mt-15"}
            product={orderEntry.product}
            mode={"15-digits"}
            packCode={orderEntry.packCode}
          />
        </Stack>
      </TableCell>
      <TableCell className={"scha__order-details-page-shipment-row__cell__dates"} style={{ height: "fit-content" }}>
        <ItemBlock
          className={"scha__print-only"}
          mainTitle={t("activeQuotePage.table.header.delivery.title")}
          secondTitle={t("order.shipment.package.goodsIssueDate")}
          mainValue={formatNullableDate(orderEntry.deliveryDate, DateFormatType.DATE)}
          secondValue={goodsIssueDate()}
        />
        <CellWithSubtitle
          className={"scha__screen-only"}
          title={formatNullableDate(orderEntry.deliveryDate, DateFormatType.DATE)}
          subtitle={goodsIssueDate()}
          lightHeader={true}
        />
      </TableCell>
      <TableCell className={"scha__order-details-page-shipment-row__cell__price"} style={{ height: "fit-content" }}>
        <ItemBlock
          className={"scha__print-only"}
          mainTitle={t("cartPage.table.header.price.title")}
          secondTitle={t("quoteDetailsPage.table.header.priceUnit")}
          mainValue={<OrderLineItemElements.NetPrice data={orderEntry} />}
        />
        <ItemBlock
          className={"scha__print-only mt-15"}
          mainTitle={t("orderDetailsPage.table.header.total")}
          mainValue={" "}
        />
        <ItemBlock className={"scha__print-only"} mainValue={<OrderLineItemElements.TotalPrice data={orderEntry} />} />
        <ItemBlock className={"scha__print-only"} mainValue={<OrderLineItemElements.Surcharge data={orderEntry} />} />
        <CellWithSubtitle
          className={"scha__screen-only"}
          title={<OrderLineItemElements.NetPrice data={orderEntry} />}
          lightHeader={true}
        />
      </TableCell>
      <TableCell className={"scha__screen-only"} style={{ height: "fit-content" }}>
        <strong>
          <OrderLineItemElements.TotalPrice data={orderEntry} />
        </strong>
        <OrderLineItemElements.Surcharge data={orderEntry} className={"mt-2"} />
      </TableCell>
    </>
  );
};

export default PackageTableProductRow;
